import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import api from '../services/api.js';
import NavBar from '../components/NavBar.jsx';

// Affiche une date de dernière activité de façon lisible
function formatLastSeen(iso) {
  const d = new Date(iso);
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMin < 1) return "à l'instant";
  if (diffMin < 60) return `il y a ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `il y a ${diffH} h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return 'hier';
  if (diffD < 7) return `il y a ${diffD} j`;
  return d.toLocaleDateString('fr-FR');
}

export default function Admin() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [tab, setTab] = useState('reports');

  // Vérifie que le compte connecté est admin
  useEffect(() => {
    let cancelled = false;
    api.getMe()
      .then(u => { if (!cancelled) { setIsAdmin(!!u.is_admin); setChecking(false); } })
      .catch(() => { if (!cancelled) { setIsAdmin(false); setChecking(false); } });
    return () => { cancelled = true; };
  }, []);

  if (checking) {
    return <div className="loading-screen"><div className="loading-rocket">🔒</div><p>Vérification…</p></div>;
  }

  if (!isAdmin) {
    return (
      <div className="container" style={{ paddingTop: 40, textAlign: 'center' }}>
        <div className="emoji" style={{ fontSize: 48 }}>🔒</div>
        <h3>Accès réservé</h3>
        <p style={{ color: 'var(--text-muted)' }}>Cet espace est réservé à l'administrateur.</p>
        <button className="btn btn-primary" onClick={() => navigate('/')} style={{ marginTop: 16 }}>← Accueil</button>
      </div>
    );
  }

  return (
    <>
      <NavBar />
      <div className="container" style={{ paddingBottom: 40 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button className="btn" onClick={() => navigate('/')} style={{ padding: '6px 14px', width: 'auto', flexShrink: 0 }}>← Accueil</button>
        <h2 style={{ margin: 0, flex: 1 }}>🛠️ Admin</h2>
      </div>

      <div className="duels-tabs" style={{ marginBottom: 16 }}>
        <button className={`duels-tab ${tab === 'reports' ? 'active' : ''}`} onClick={() => setTab('reports')}>
          🚩 Signalements
        </button>
        <button className={`duels-tab ${tab === 'users' ? 'active' : ''}`} onClick={() => setTab('users')}>
          👥 Utilisateurs
        </button>
      </div>

      {tab === 'reports' ? <ReportsPanel /> : <UsersPanel />}
      </div>
    </>
  );
}

function ReportsPanel() {
  const [reports, setReports] = useState([]);
  const [includeResolved, setIncludeResolved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async (withResolved) => {
    setLoading(true); setError('');
    try {
      const r = await api.getReports(withResolved);
      setReports(r.reports || []);
    } catch (e) { setError(e?.message || 'Erreur'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(includeResolved); }, []);

  const toggleResolved = () => {
    const next = !includeResolved;
    setIncludeResolved(next);
    load(next);
  };

  const handleResolve = async (id) => {
    try { await api.resolveReport(id); load(includeResolved); }
    catch (e) { setError(e?.message || 'Erreur'); }
  };

  if (loading) return <div className="loading-screen"><p>Chargement…</p></div>;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12 }}>
        <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{reports.length} signalement{reports.length > 1 ? 's' : ''}</span>
        <button className="btn" onClick={toggleResolved} style={{ padding: '6px 14px', fontSize: '0.85rem', width: 'auto', flexShrink: 0, whiteSpace: 'nowrap' }}>
          {includeResolved ? 'Masquer traités' : 'Voir traités'}
        </button>
      </div>
      {error && <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>}
      {reports.length === 0 ? (
        <div className="cards-empty" style={{ marginTop: 40 }}><div className="emoji">✅</div><h3>Aucun signalement</h3></div>
      ) : reports.map(r => (
        <div key={r.id} className="fact-card" style={{ marginBottom: 12, opacity: r.resolved ? 0.55 : 1 }}>
          {r.qid && (
            <div style={{
              display: 'inline-block', fontFamily: 'monospace', fontSize: '0.78rem',
              color: 'var(--gold)', background: 'rgba(242,192,99,0.12)',
              border: '1px solid rgba(242,192,99,0.3)', borderRadius: 6,
              padding: '2px 8px', marginBottom: 6, userSelect: 'all', cursor: 'text',
            }}>{r.qid}</div>
          )}
          <div style={{ fontWeight: 700, marginBottom: 6 }}>{r.question_text || '(question supprimée)'}</div>
          {r.current_question_text && r.current_question_text !== r.question_text && (
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 6 }}>
              Texte actuel : {r.current_question_text}
            </div>
          )}
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            {r.reporter ? `Signalé par ${r.reporter}` : 'Anonyme'}
            {r.created_at ? ` — ${new Date(r.created_at).toLocaleString('fr-FR')}` : ''}
          </div>
          {r.reason && <div style={{ marginTop: 6, fontStyle: 'italic' }}>« {r.reason} »</div>}
          {!r.resolved && (
            <button className="btn btn-primary" onClick={() => handleResolve(r.id)}
              style={{ marginTop: 10, padding: '6px 14px', fontSize: '0.85rem' }}>✅ Marquer traité</button>
          )}
          {r.resolved && <div style={{ marginTop: 8, color: 'var(--text-muted)', fontSize: '0.8rem' }}>✅ Traité</div>}
        </div>
      ))}
    </>
  );
}

function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const r = await api.adminListUsers(true);
      setUsers(r.users || []);
    } catch (e) { setError(e?.message || 'Erreur'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (u) => {
    const action = u.is_active ? 'désactiver' : 'réactiver';
    if (u.is_active && !window.confirm(`Désactiver le compte "${u.display_name}" ? Il ne pourra plus se connecter (réversible).`)) return;
    setBusy(u.id); setError('');
    try {
      if (u.is_active) await api.adminDeactivateUser(u.id);
      else await api.adminReactivateUser(u.id);
      await load();
    } catch (e) { setError(e?.message || `Impossible de ${action} ce compte`); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="loading-screen"><p>Chargement…</p></div>;

  const activeCount = users.filter(u => u.is_active).length;
  return (
    <>
      {error && <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>}
      <div style={{ color: 'var(--text-muted)', marginBottom: 12 }}>{users.length} utilisateur{users.length > 1 ? 's' : ''} · {activeCount} actif{activeCount > 1 ? 's' : ''}</div>
      {users.map(u => (
        <div key={u.id} className="fact-card" style={{ marginBottom: 10, opacity: u.is_active ? 1 : 0.5, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ position: 'relative', fontSize: 28 }}>
            {u.avatar || '🧑‍🚀'}
            {u.online && <span title="En ligne" style={{ position: 'absolute', bottom: 0, right: -2, width: 10, height: 10, borderRadius: '50%', background: '#3ddc84', border: '2px solid var(--bg, #1a1a2e)' }} />}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700 }}>
              {u.display_name}
              {u.is_admin && <span style={{ marginLeft: 6, fontSize: '0.7rem', color: 'var(--gold, #fbbf24)' }}>★ admin</span>}
              {u.premium && <span style={{ marginLeft: 6, fontSize: '0.7rem', color: 'var(--accent, #4dd0e1)' }}>💎 premium</span>}
              {u.is_technical && <span style={{ marginLeft: 6, fontSize: '0.7rem', color: 'var(--text-muted)' }}>🔧 démo</span>}
              {!u.is_active && <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#ff7a6b' }}>désactivé</span>}
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>ELO {u.elo_rating}</div>
            <div style={{ fontSize: '0.72rem', color: u.online ? '#3ddc84' : 'var(--text-muted)' }}>
              {u.online ? '🟢 En ligne' : u.last_seen ? `Vu ${formatLastSeen(u.last_seen)}` : 'Jamais joué'}
            </div>
          </div>
          <button
            className="btn"
            onClick={() => toggleActive(u)}
            disabled={busy === u.id}
            style={{ padding: '6px 12px', fontSize: '0.8rem', borderColor: u.is_active ? 'rgba(255,107,107,0.4)' : 'rgba(0,200,100,0.4)', color: u.is_active ? '#ff7a6b' : '#3ddc84' }}>
            {busy === u.id ? '…' : u.is_active ? 'Désactiver' : 'Réactiver'}
          </button>
        </div>
      ))}
    </>
  );
}
